import React, { useEffect, useState } from 'react';
import Logo from "../public/hi-way-89-logo.webp"

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeItem, setActiveItem] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const menuItems = ['About', 'Roster', 'Services',];

  const handleItemClick = (item) => {
    setActiveItem(item);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-black shadow-lg py-3 border-b border-gray-200' 
          : 'bg-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center group bg-white">
            <a href='/' className="transition-transform duration-300 group-hover:scale-105">
              <img 
                src={Logo} 
                className={`transition-all duration-300 max ${
                  isScrolled ? 'w-12 max-sm:w-7' : 'w-20 max-sm:w-10'
                }`} 
                alt="Logo"
              />
            </a>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-12">
            {menuItems.map((item) => (
              <div key={item} className="relative group">
                <a
                  href={`${item.toLowerCase()}`}
                  className={`relative text-sm font-semibold tracking-wider transition-all duration-300 ${
                    activeItem === item 
                      ? 'text-black' 
                      : isScrolled 
                        ? 'text-white hover:text-gray-300' 
                        : 'text-white hover:text-gray-200'
                  }`}
                  onClick={() => handleItemClick(item)}
                >
                  {item.toUpperCase()}
                  
                  {/* Double underline effect */}
                  <span className={`absolute -bottom-2 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full ${
                    activeItem === item ? 'w-full' : ''
                  }`}></span>
                  <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-white transition-all duration-500 group-hover:w-full ${
                    activeItem === item ? 'w-full' : ''
                  }`}></span>
                </a>
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden relative w-10 h-10 flex flex-col items-center justify-center group z-60"
          >
            <div className="w-6 h-6 flex flex-col justify-center space-y-1.5">
              <span 
                className={`block w-full h-0.5 transition-all duration-300 ${
                  isMobileMenuOpen 
                    ? 'rotate-45 translate-y-2 bg-white' 
                    : isScrolled 
                      ? 'bg-white group-hover:bg-white' 
                      : 'bg-white group-hover:bg-gray-200'
                }`}
              ></span>
              <span 
                className={`block w-full h-0.5 transition-all duration-200 ${
                  isMobileMenuOpen 
                    ? 'opacity-0' 
                    : isScrolled 
                      ? 'bg-white group-hover:bg-white' 
                      : 'bg-white group-hover:bg-gray-200'
                }`}
              ></span>
              <span 
                className={`block w-full h-0.5 transition-all duration-300 ${
                  isMobileMenuOpen 
                    ? '-rotate-45 -translate-y-2 bg-white' 
                    : isScrolled 
                      ? 'bg-white group-hover:bg-white' 
                      : 'bg-white group-hover:bg-gray-200'
                }`}
              ></span>
            </div>
          </button>
        </div>

        {/* Full Height Fixed Mobile Menu Overlay */}
        <div className={`md:hidden fixed inset-0 z-50 transition-all duration-500 ${
          isMobileMenuOpen 
            ? 'opacity-100 pointer-events-auto' 
            : 'opacity-0 pointer-events-none'
        }`}>
          {/* Background Overlay */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-95 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
          
          {/* Menu Content */}
          <div className="relative z-10 h-full flex flex-col">
            {/* Header with Logo and Close Button */}
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <div className="flex items-center">
                <img 
                  src={Logo}
                  className="w-12"
                  alt="Logo"
                />
                <div className="ml-4">
                  <h2 className="text-white text-xl font-black">HI-WAY 89 ENTERTAINMENT</h2>
                  <p className="text-gray-400 text-sm">RECORD LABEL</p>
                </div>
              </div>
              
              {/* Close Button */}
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-10 h-10 flex items-center justify-center text-white hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Navigation Items */}
            <div className="flex-1 flex flex-col justify-center px-6">
              <div className="space-y-8">
                {menuItems.map((item) => (
                  <a
                    key={item}
                    href={`/${item.toLowerCase()}`}
                    className={`block text-2xl font-black tracking-wide transition-all duration-300 transform hover:translate-x-4 ${
                      activeItem === item 
                        ? 'text-white border-l-4 border-white pl-6 py-3' 
                        : 'text-gray-400 hover:text-white pl-6 py-3'
                    }`}
                    onClick={() => handleItemClick(item)}
                  >
                    {item.toUpperCase()}
                  </a>
                ))}
              </div>
            </div>

            {/* Footer Section */}
            <div className="p-6 border-t border-gray-800">
              <div className="text-center text-gray-400 text-sm">
                <p>Canada • WORLDWIDE</p>
                <p className="mt-2">© 2025 HI-WAY 89 ENTERTAINMENT RECORDS</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;